/**
 * Query keys factory for TanStack Query
 * Centralized query key management for type-safe cache invalidation
 */

import type { ConversationStatus } from '@/types/conversations'
import type { PhoneCallsFilters } from '@/types/phone-calls'

/**
 * Conversation filters interface
 */
export interface ConversationFilters {
  status?: ConversationStatus
  source?: string
  category?: string
  tags?: string
  tag_ids?: string
  lead_source?: string
  phone_number_id?: string
  threads?: 'head' | 'all'
  pageSize?: number
}

/**
 * Query keys factory
 * Provides type-safe query keys for all queries
 */
export const queryKeys = {
  brain: {
    all: ['brain'] as const,
    conversations: () => [...queryKeys.brain.all, 'conversations'] as const,
    models: () => [...queryKeys.brain.all, 'models'] as const,
  },
  meta: {
    all: ['meta'] as const,
    facebookStatus: () => [...queryKeys.meta.all, 'facebook-status'] as const,
    whatsappStatus: () => [...queryKeys.meta.all, 'whatsapp-status'] as const,
  },
  conversations: {
    all: ['conversations'] as const,
    lists: () => [...queryKeys.conversations.all, 'list'] as const,
    list: (filters: ConversationFilters = {}) => 
      [...queryKeys.conversations.lists(), filters] as const,
    details: () => [...queryKeys.conversations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.conversations.details(), id] as const,
    messages: (id: string, page?: number, pageSize?: number) => 
      [...queryKeys.conversations.detail(id), 'messages', { page, pageSize }] as const,
  },
  phoneCalls: {
    all: ['phone-calls'] as const,
    lists: () => [...queryKeys.phoneCalls.all, 'list'] as const,
    list: (filters: PhoneCallsFilters = {}) => 
      [...queryKeys.phoneCalls.lists(), filters] as const,
    details: () => [...queryKeys.phoneCalls.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.phoneCalls.details(), id] as const,
  },
  documents: {
    all: ['documents'] as const,
    raw: (filters?: { category?: string; subcategory?: string }) =>
      [...queryKeys.documents.all, 'raw', filters || {}] as const,
    lists: () => [...queryKeys.documents.all, 'list'] as const,
    list: (filters?: { category?: string; subcategory?: string }) => 
      [...queryKeys.documents.lists(), filters || {}] as const,
    details: () => [...queryKeys.documents.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.documents.details(), id] as const,
    agents: (id: number) => [...queryKeys.documents.all, 'agents', id] as const,
  },
  // pantallas-abilities-y-skills
  abilities: {
    all: ['abilities'] as const,
    ofAgent: (agentId: string) => ['abilities', 'agent', agentId] as const,
  },
  skills: {
    all: ['skills'] as const,
  },
  urls: {
    all: ['urls'] as const,
    lists: () => [...queryKeys.urls.all, 'list'] as const,
    list: (filters?: { category?: string; subcategory?: string }) => 
      [...queryKeys.urls.lists(), filters || {}] as const,
    details: () => [...queryKeys.urls.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.urls.details(), id] as const,
  },
  knowledge: {
    all: ['knowledge'] as const,
    categoriesBundle: () => [...queryKeys.knowledge.all, 'categories-bundle'] as const,
    directoriesTree: () => [...queryKeys.knowledge.all, 'directories-tree'] as const,
    settings: () => [...queryKeys.knowledge.all, 'settings'] as const,
  },
  documentSearch: {
    all: ['document-search'] as const,
    search: (query: string, limit?: number, filters?: Record<string, unknown>) => 
      [...queryKeys.documentSearch.all, 'search', { query, limit, filters }] as const,
  },
} as const
